import React, { useRef, useState, useMemo, useEffect } from "react";
import {
  Edit,
  useGetOne,
  useGetIdentity,
  useDataProvider,
  FormDataConsumer,
  useRedirect,
  SimpleForm,
  useGetList,
  TextInput,
  DateInput,
  required,
  NumberInput,
  SelectInput,
  ReferenceInput,
  useRecordContext,
} from "react-admin";
import { Grid } from "@mui/material";
import moment from "moment";
import "moment/locale/fr";
import { useLocation } from "react-router-dom";
import { useFormContext } from "react-hook-form";

// ---- Auto-fill du prix depuis fish_prices
const FishPriceAutoFill = ({ campaign, specieFish, fishingState }) => {
  const dataProvider = useDataProvider();
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (!campaign || !specieFish) return;

    const run = async () => {
      // ne pas écraser une saisie manuelle ≠ 10 (valeur par défaut)
      const current = getValues("price");
      if (
          current !== undefined &&
          current !== null &&
          `${current}` !== "" &&
          Number(current) !== 10
      )
        return;

      try {
        const { data } = await dataProvider.getList("fish_prices", {
          filter: { campaign, specieFish },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "DESC" },
        });

        let price = 10;
        if (data && data.length > 0) {
          const fp = data[0];
          if (fishingState === "/fishing_states/4") {
            // "A l'étang" (ou selon ta convention)
            price = Number(fp.costPrice ?? 10);
          } else {
            // vendu / gardé -> on priorise disposal puis purchase puis cost
            price = Number(
                fp.disposalFeeCollector ??
                fp.purchaseRateCollector ??
                fp.costPrice ??
                10
            );
          }
        }
        const cur2 = getValues("price");
        if (
            cur2 === undefined ||
            cur2 === null ||
            `${cur2}` === "" ||
            Number(cur2) === 10
        ) {
          setValue("price", price, { shouldDirty: false });
        }
      } catch {
        const cur = getValues("price");
        if (cur === undefined || cur === null || `${cur}` === "") {
          setValue("price", 10, { shouldDirty: false });
        }
      }
    };

    run();
  }, [campaign, specieFish, fishingState, dataProvider, setValue, getValues]);

  return null;
};

// --- helpers
const iriOf = (type, id) => `/${type}/${id}`;
const extractIdFromIri = (iri) => {
  if (!iri || typeof iri !== "string") return null;
  const n = parseInt(iri.split("/").pop(), 10);
  return Number.isFinite(n) ? n : null;
};

const FishEdit = () => {
  const dataProvider = useDataProvider();
  const { data: identity } = useGetIdentity();
  const redirect = useRedirect();
  const location = useLocation();
  const formDataRef = useRef(null);

  // campagnes -> choices "YYYY / YYYY"
  const { data: campData } = useGetList("campaigns", {
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 1000 },
  });
  const choices = useMemo(() => {
    if (!campData) return [];
    moment.locale("fr");
    return campData.map((c) => ({
      id: c.id,
      name: `${moment(c.startDate).format("YYYY")} / ${moment(c.endDate).format(
          "YYYY"
      )}`,
    }));
  }, [campData]);

  // état local pour savoir quelle destination a été touchée (pour nettoyer collector / waterBodyFishingState)
  const [fishingStateSuccess, setFishingStateSuccess] = useState(null);

  // isBasin depuis la querystring
  const searchParams = new URLSearchParams(location.search);
  const isBasin = searchParams.get("isBasin") === "true";
  function GetNameSpieces(id) {
    const  spieciesName = useGetOne('specie_fishes', { id: id });
    if(spieciesName.data)  {
      return spieciesName.data.name;
    } else {
      return spieciesName.data;
    }
  }
  const onSuccess = async (response) => {
    try {
      const rec = response ?? {};
      const actionId =
          rec?.id ?? (rec?.["@" + "id"] ? extractIdFromIri(rec["@id"]) : null);

      // prix saisi dans le formulaire (via ref)
      const fd = formDataRef.current || {};
      const formPrice =
          fd?.price != null && `${fd.price}`.trim() !== ""
              ? Number(fd.price)
              : null;

      if (formPrice != null && !Number.isNaN(formPrice)) {
        // si l'action a déjà un fishPrice
        const existingFpId =
            typeof rec.fishPrice === "string"
                ? extractIdFromIri(rec.fishPrice)
                : rec.fishPrice?.id ?? null;

        // quel champ mettre à jour selon l’état
        const fieldToUpdate =
            rec.fishingState === "/fishing_states/4"
                ? "costPrice"
                : "disposalFeeCollector";

        let updated = false;

        if (existingFpId) {
          try {
            const existing = await dataProvider.getOne("fish_prices", {
              id: existingFpId,
            });
            const patch = { [fieldToUpdate]: formPrice };
            if (fieldToUpdate === "disposalFeeCollector") {
              patch.purchaseRateCollector = formPrice;
            }
            await dataProvider.update("fish_prices", {
              id: existingFpId,
              data: patch,
              previousData: existing.data,
            });
            updated = true;
          } catch {
            updated = false;
          }
        }

        // si pas pu updater: créer un fish_price et le rattacher à l’action
        if (!updated) {
          const created = await dataProvider.create("fish_prices", {
            data: {
              campaign: rec.campaign,
              specieFish: rec.specieFish,
              user: identity?.id ? `/users/${identity.id}` : undefined,
              costPrice: formPrice,
              disposalFeeCollector: formPrice,
              purchaseRateCollector: formPrice,
            },
          });
          const cData = created?.data ?? created;
          const iri =
              cData?.["@id"] ?? (cData?.id ? iriOf("fish_prices", cData.id) : null);

          if (actionId && iri) {
            await dataProvider.update("action_fishes", {
              id: actionId,
              data: { fishPrice: iri },
              previousData: rec,
            });
          }
        }
      }

      // nettoyage conditionnel selon sélection utilisateur pendant l’édition
      const record = formDataRef.current?.__recordForRedirect;
      if (record) {
        if (
            fishingStateSuccess === "/fishing_states/2" &&
            record.waterBodyFishingState != null
        ) {
          await dataProvider.update("action_fishes", {
            id: record.id,
            data: { waterBodyFishingState: null },
          });
        } else if (
            fishingStateSuccess === "/fishing_states/1" &&
            record.collector != null
        ) {
          await dataProvider.update("action_fishes", {
            id: record.id,
            data: { collector: null },
          });
        }
      }

      const basinParam = isBasin ? "&isBasin=true" : "";
      const waterBodyIdForRedirect =
          record?.waterBody ?? rec?.waterBody ?? formDataRef.current?.waterBody;
      redirect({
        pathname: "/action_fishes",
        search: `filter=${encodeURIComponent(
            JSON.stringify({ waterBody: waterBodyIdForRedirect })
        )}${basinParam}`,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      // TODO: useNotify si tu veux une alerte UI
    }
  };

  const formatSpecieFish = (rec) => (rec?.stage ? rec.stage : "Stade non connu");

  // Titre + récupération du nom de plan d'eau à l'intérieur du contexte d'édition
  const Title = () => {
    const record = useRecordContext();
    const waterBodyId = record?.waterBody;
    const { data: waterBody } = useGetOne(
        "water_bodies",
        { id: waterBodyId },
        { enabled: !!waterBodyId }
    );
    return <h1 className={"titleName"}>{waterBody?.name ?? ""}</h1>;
  };

  // Sauvegarder le record courant dans la ref (pour onSuccess)
  const CaptureRecordInRef = () => {
    const record = useRecordContext();
    formDataRef.current = {
      ...(formDataRef.current || {}),
      __recordForRedirect: record,
    };
    return null;
  };

  return (
      <Edit mutationOptions={{ onSuccess }} mutationMode="pessimistic">
        <Title />
        <SimpleForm sanitizeEmptyValues defaultValues={{ price: 10 }}>
          {/* 1) Auto-fill du prix + capture du record */}
          <FormDataConsumer>
            {({ formData }) => (
                <>
                  <FishPriceAutoFill
                      campaign={formData.campaign}
                      specieFish={formData.specieFish}
                      fishingState={formData.fishingState}
                  />
                  <CaptureRecordInRef />
                </>
            )}
          </FormDataConsumer>

          {/* 2) Le reste du formulaire, hors du FormDataConsumer ci-dessus */}
          {/* Campagne */}
          <Grid container>
            <Grid item xs={6}>
              <SelectInput
                  source="campaign"
                  choices={choices}
                  label="Campagne"
                  validate={[required()]}
                  fullWidth
              />
            </Grid>
          </Grid>

          {/* Affiliation si bassin */}
          {isBasin && (
              <Grid container>
                <Grid item xs={6}>
                  <SelectInput
                      source="affiliation"
                      label="Affiliation de l'action"
                      choices={[
                        { id: "Production estivale 1", name: "Production estivale 1" },
                        { id: "Production estivale 2", name: "Production estivale 2" },
                        { id: "Stockage hivernal 1", name: "Stockage hivernal 1" },
                        { id: "Stockage hivernal 2", name: "Stockage hivernal 2" },
                      ]}
                      validate={[required()]}
                      fullWidth
                  />
                </Grid>
              </Grid>
          )}

          {/* Sélection d'espèce (uniq) */}
          <Grid container>
            <Grid item xs={6}>
              <ReferenceInput source="specieFish"  reference="specie_fishes/uniq">
                <SelectInput optionText="name" label="Espèce" validate={[required()]} fullWidth />
              </ReferenceInput>
            </Grid>
          </Grid>

          {/* Liste des stades filtrée par l'espèce choisie */}
          <FormDataConsumer>
            {({ formData, ...rest }) => formData.specieFish &&
                <Grid container>
                  <Grid item xs={6}>
                    <ReferenceInput source="specieFish" reference={"specie_fishes?name="+GetNameSpieces(formData.specieFish)}>
                      <SelectInput  optionText={formatSpecieFish} label="Stade espèce"  fullWidth />
                    </ReferenceInput>
                  </Grid>
                </Grid>
            }
          </FormDataConsumer>

          {/* Nombres / Poids */}
          <Grid container>
            <Grid item xs={6} className={"itemForm1"}>
              <NumberInput source="numberHead" label="Nombre de tête(s)" fullWidth onWheel={(e) => e.target.blur()}  />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={3} className={"itemForm1"}>
              <NumberInput source="averageWeight" label="Poids moyen (en kg)" fullWidth onWheel={(e) => e.target.blur()}  />
            </Grid>
            <Grid item xs={3} className={"itemForm2"}>
              <NumberInput
                  source="totalWeight"
                  label="Poids total (en kg)"
                  validate={[required()]}
                  fullWidth
                  onWheel={(e) => e.target.blur()}
              />
            </Grid>
          </Grid>

          {/* Destination */}
          <Grid container>
            <Grid item xs={6}>
              <ReferenceInput source="fishingState" reference="fishing_states">
                <SelectInput optionText="name" label="Destination" fullWidth validate={[required()]} />
              </ReferenceInput>
            </Grid>
          </Grid>

          {/* Collecteur si vendu */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.fishingState === "/fishing_states/2" && (
                    <Grid container>
                      <Grid item xs={6}>
                        <ReferenceInput source="collector" reference="collectors">
                          <SelectInput
                              optionText="name"
                              label="Collecteur"
                              fullWidth
                              onChange={() => setFishingStateSuccess("/fishing_states/2")}
                          />
                        </ReferenceInput>
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* WaterBody cible si gardé */}
          <FormDataConsumer>
            {({ formData }) =>
                formData.fishingState === "/fishing_states/1" && (
                    <Grid container>
                      <Grid item xs={6}>
                        <ReferenceInput source="waterBodyFishingState" reference="water_bodies" perPage={5000}>
                          <SelectInput
                              optionText="name"
                              label="Liste étangs/bassins"
                              fullWidth
                              onChange={() => setFishingStateSuccess("/fishing_states/1")}
                          />
                        </ReferenceInput>
                      </Grid>
                    </Grid>
                )
            }
          </FormDataConsumer>

          {/* Date */}
          <Grid container>
            <Grid item xs={6} className={"itemForm1"}>
              <DateInput source="date" label="Date" validate={[required()]} fullWidth />
            </Grid>
          </Grid>

          {/* Tarif saisi manuellement (on s’en sert en onSuccess) */}
          <FormDataConsumer>
            {({ formData }) => {
              // garder une ref du formData pour onSuccess
              formDataRef.current = { ...(formDataRef.current || {}), ...formData };
              const showPrice = !!(formData.specieFish && formData.campaign);
              return (
                  showPrice && (
                      <Grid container>
                        <Grid item xs={6}>
                          <NumberInput source="price" label="Coût unitaire (en € HT /kg)" fullWidth  onWheel={(e) => e.target.blur()} />
                        </Grid>
                      </Grid>
                  )
              );
            }}
          </FormDataConsumer>

          {/* Note */}
          <Grid container>
            <Grid item xs={6}>
              <TextInput source="note" label="Remarque(s)" multiline fullWidth />
            </Grid>
          </Grid>

          {/* WaterBody (caché) */}
          <TextInput source="waterBody" className={"hidden"} type={"hidden"} />
        </SimpleForm>
      </Edit>
  );
};

export default FishEdit;
